import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '../../../context/AppContext';
import Tooltip from '@mui/material/Tooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MobileStepper from '@mui/material/MobileStepper';
import { useMediaQuery } from '@mui/material';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import axios from 'axios';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import 'dayjs/locale/en-gb';
import './AddMeasureModal.css';
import LoadingOverlay from '../../LoadingOverlay/LoadingOverlay';
import TourButton from '../../TourButton/TourButton';
import { getAddMeasureModalSteps } from '../../../tours/addMeasureModal';
import { useNotification } from '../../notifications/useNotification';
import AuthenticatedImage from '../../AuthenticatedImage/AuthenticatedImage';

/**
 * Modal for adding a new body measurement or editing an existing one.
 *
 * When opened in **create** mode (no `data` prop), the modal automatically
 * fetches the client's most recent measurement and pre-fills the height field
 * with the last recorded value, since height rarely changes between sessions.
 * BMI is computed live from the current `bodyWeight` and `height` fields.
 *
 * The date is entered as three separate DD / MM / YYYY inputs and combined
 * internally into a YYYY-MM-DD string for API submission.
 *
 * @component
 * @param {object}   props
 * @param {Function} props.setShowModal - Callback to show/hide the modal.
 * @param {Function} props.onSubmit    - Async callback invoked after a successful save.
 * @param {object}   props.client      - The client object; must contain `_id`.
 * @param {object}   [props.data]      - Existing measurement data when editing.
 */
const AddMeasureModal = ({ setShowModal, onSubmit, onDelete, client, data }) => {
    const { t } = useTranslation();
    const tourSteps = useMemo(() => getAddMeasureModalSteps(t), [t]);
    const { currentLanguage } = useAppContext();
    const { showError, showWarning } = useNotification();
    const [loading, setLoading] = useState(false);
    const isMobile = useMediaQuery('(max-width: 768px)');
    const [errors, setErrors] = useState({});

    /* ── Photo state ──────────────────────────────────────────────── */
    const [photoFiles, setPhotoFiles] = useState([]);                                   // new files to upload
    const [photoPreviewUrls, setPhotoPreviewUrls] = useState([]);                       // object URLs for new files
    const [existingPhotoUrls, setExistingPhotoUrls] = useState(data?.photoUrls ?? []);  // URLs already saved in DB
    const [photoCarouselIndex, setPhotoCarouselIndex] = useState(0);
    const photoInputRef = useRef(null);

    /** Combined photo list: existing URLs first, then previews for newly added files */
    const allPhotos = [...existingPhotoUrls, ...photoPreviewUrls];

    const adapterLocale = currentLanguage === 'it' ? 'it' : 'en-gb';
    const calendarTheme = createTheme({
        palette: { primary: { main: '#fd4701', light: '#fc9f5b', dark: '#830102' } },
    });

    const [formData, setFormData] = useState({
        date:       data ? dayjs(data.date) : dayjs(),
        bodyWeight: data ? (data.bodyWeight ?? '') : '',
        bodyFat:    data ? (data.bodyFat    ?? '') : '',
        height:     data ? (data.height     ?? '') : '',
        waist:      data ? (data.waist      ?? '') : '',
        hips:       data ? (data.hips       ?? '') : '',
        leftArm:    data ? (data.leftArm    ?? '') : '',
        rightArm:   data ? (data.rightArm   ?? '') : '',
        leftThigh:  data ? (data.leftThigh  ?? '') : '',
        rightThigh: data ? (data.rightThigh ?? '') : '',
        chest:      data ? (data.chest      ?? '') : '',
        shoulders:  data ? (data.shoulders  ?? '') : '',
        neck:       data ? (data.neck       ?? '') : '',
    });

    /** Live BMI computed from bodyWeight + height fields */
    const bmi = (() => {
        const w = parseFloat(formData.bodyWeight);
        const h = parseFloat(formData.height);
        if (!w || !h || h <= 0) return '—';
        return (w / Math.pow(h / 100, 2)).toFixed(1);
    })();

    /** Pre-fill height from last measurement when creating a new record */
    useEffect(() => {
        if (data) return;
        const prefillHeight = async () => {
            try {
                const url = `${process.env.REACT_APP_BACKEND_URL}/clients/${client._id}/measurements?lastMeasurement=true`;
                const response = await axios.get(url, { withCredentials: true });
                const last = response.data;
                if (last?.height) setFormData(prev => ({ ...prev, height: last.height }));
            } catch (e) {
                console.warn('Could not prefill height from last measurement:', e);
            }
        };
        prefillHeight();
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleDateChange = (value) => {
        setFormData(prev => ({ ...prev, date: value }));
    };

    /**
     * Handles file selection from the photo input.
     * Enforces the 10-photo maximum and generates object URL previews.
     */
    const handlePhotoAdd = (e) => {
        const files = Array.from(e.target.files);
        const remaining = 10 - allPhotos.length;
        if (remaining <= 0) return;
        const accepted = files.slice(0, remaining);
        setPhotoFiles(prev => [...prev, ...accepted]);
        setPhotoPreviewUrls(prev => [...prev, ...accepted.map(f => URL.createObjectURL(f))]);
        e.target.value = '';
    };

    /**
     * Removes a photo by its index in the combined `allPhotos` array.
     * Revokes the object URL for newly added files to avoid memory leaks.
     *
     * @param {number} index - Index in the combined `allPhotos` array
     */
    const handlePhotoRemove = (index) => {
        const existingCount = existingPhotoUrls.length;
        if (index < existingCount) {
            setExistingPhotoUrls(prev => prev.filter((_, i) => i !== index));
        } else {
            const newIdx = index - existingCount;
            URL.revokeObjectURL(photoPreviewUrls[newIdx]);
            setPhotoFiles(prev => prev.filter((_, i) => i !== newIdx));
            setPhotoPreviewUrls(prev => prev.filter((_, i) => i !== newIdx));
        }
        setPhotoCarouselIndex(prev => Math.min(prev, Math.max(0, allPhotos.length - 2)));
    };

    const validateForm = () => {
        const newErrors = {};

        let allEmpty = true;
        for (const [, value] of Object.entries(formData)) {
            if (value !== '') { allEmpty = false; break; }
        }
        if (allEmpty) { showWarning(t('all-empty-fields-error')); return false; }

        if (formData.date && formData.date.isAfter(dayjs(), 'day'))
            newErrors.date = t('errorMessages.dateCantBeGreaterThanToday');
        if (formData.bodyFat !== '' && (parseFloat(formData.bodyFat) > 100 || parseFloat(formData.bodyFat) < 0))
            newErrors.bodyFat = t('errorMessages.outOfRange');

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        let allEmpty = true;
        for (const key of Object.keys(formData)) {
            if (key !== 'date' && formData[key] !== '') { allEmpty = false; break; }
        }
        if (allEmpty && photoFiles.length === 0) { handleClose(); return; }

        if (validateForm()) {
            try {
                setLoading(true);
                const bmiValue = parseFloat(bmi);

                // Use FormData so photo files can be sent as multipart
                const fd = new FormData();
                fd.append('date',       formData.date ? formData.date.format('YYYY-MM-DD') : '');
                ['bodyWeight', 'bodyFat', 'height', 'waist', 'hips',
                 'leftArm', 'rightArm', 'leftThigh', 'rightThigh', 'chest', 'shoulders', 'neck']
                    .forEach(field => {
                        if (formData[field] !== '') fd.append(field, formData[field]);
                    });
                if (!isNaN(bmiValue) && bmiValue > 0) fd.append('bmi', String(bmiValue));
                // clientId must come BEFORE file parts so multer can parse it reliably
                if (!(data && Object.keys(data).length > 0)) {
                    fd.append('clientId', client._id);
                }
                // Existing photo URLs to keep (those not removed by the user)
                existingPhotoUrls.forEach(url => fd.append('existingPhotoUrls', url));
                // New photo files
                photoFiles.forEach(file => fd.append('photos', file));

                const url = `${process.env.REACT_APP_BACKEND_URL}/measurements`;
                if (data && Object.keys(data).length > 0) {
                    await axios.put(`${url}/${data._id}`, fd, { withCredentials: true });
                } else {
                    await axios.post(url, fd, { withCredentials: true });
                }
                await onSubmit();
                handleClose();
            } catch (e) {
                console.error('AddMeasureModal error:', e);
                showError(t('unknown-error'));
            } finally {
                setLoading(false);
            }
        }
    };

    const handleClose = () => {
        // Revoke any object URLs created for preview to free memory
        photoPreviewUrls.forEach(u => URL.revokeObjectURL(u));
        setFormData({ date: null, bodyWeight: '', bodyFat: '', height: '', waist: '', hips: '',
            leftArm: '', rightArm: '', leftThigh: '', rightThigh: '', chest: '', shoulders: '', neck: '' });
        setPhotoFiles([]);
        setPhotoPreviewUrls([]);
        setExistingPhotoUrls([]);
        setPhotoCarouselIndex(0);
        setShowModal(false);
    };

    const handleDelete = async () => {
        if (!data?._id) return;
        try {
            setLoading(true);
            await axios.delete(
                `${process.env.REACT_APP_BACKEND_URL}/measurements/${data._id}`,
                { withCredentials: true }
            );
            if (onDelete) await onDelete();
            handleClose();
        } catch (e) {
            console.error('DeleteMeasure error:', e);
            showError(t('unknown-error'));
        } finally {
            setLoading(false);
        }
    };

    /** Renders a labelled numeric measurement field with a unit badge */
    const measureField = (label, name, placeholder, unit, error) => (
        <div className="amm-field">
            <label className="amm-label">{label}</label>
            <div className="amm-input-unit-wrap">
                <input
                    className="amm-input"
                    type="number"
                    name={name}
                    value={formData[name]}
                    onChange={handleInputChange}
                    placeholder={placeholder}
                />
                {unit && <span className="amm-unit">{unit}</span>}
            </div>
            {error && <span className="amm-error">{error}</span>}
        </div>
    );

    return (
        <>
            <LoadingOverlay open={loading} />
            <div className="amm-overlay">
                <div className="amm-modal" data-tour="amm-modal">

                    {/* ── Header ── */}
                    <div className="amm-header">
                        <h2 className="amm-title">{data && Object.keys(data).length > 0 ? t('edit-measure') : t('add-new-measure')}</h2>
                        <div className="amm-tutorial-btn">
                            <TourButton steps={tourSteps} iconOnly={isMobile} label={t('tour.tutorial-btn')} />
                        </div>
                        <button className="amm-close-btn" onClick={handleClose} aria-label="Close">
                            <span className="material-symbols-outlined">close</span>
                        </button>
                    </div>

                    {/* ── Body ── */}
                    <div className="amm-body">

                        {/* Date picker */}
                        <div data-tour="amm-date">
                        <ThemeProvider theme={calendarTheme}>
                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={adapterLocale}>
                                <DatePicker
                                    label={t('Date')}
                                    value={formData.date}
                                    onChange={handleDateChange}
                                    slotProps={{
                                        textField: {
                                            size: 'small',
                                            fullWidth: true,
                                            error: !!errors.date,
                                            helperText: errors.date,
                                            sx: {
                                                '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#fc9f5b',
                                                },
                                            },
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                        </ThemeProvider>
                        </div>

                        {/* BMI display */}
                        <div className="amm-bmi-card" data-tour="amm-bmi">
                            <Tooltip title={t('bmi-info')} placement="right" arrow>
                                <span className="amm-bmi-label">
                                    {t('BMI')}
                                    <InfoOutlinedIcon sx={{ fontSize: 16, color: '#94a3b8', ml: 0.5 }} />
                                </span>
                            </Tooltip>
                            <span className="amm-bmi-value">{bmi}</span>
                        </div>

                        {/* Measurement fields */}
                        <div data-tour="amm-measurements">
                        <div className="amm-row">
                            {measureField(t('body-weight'), 'bodyWeight', t('enter-body-weight'), 'kg', errors.bodyWeight)}
                            {measureField(t('body-fat'), 'bodyFat', t('enter-body-fat'), '%', errors.bodyFat)}
                        </div>
                        <div className="amm-row">
                            {measureField(t('Height'), 'height', t('enter-height'), 'cm', errors.height)}
                            {measureField(t('Waist'), 'waist', t('enter-waist'), 'cm', errors.waist)}
                        </div>
                        <div className="amm-row">
                            {measureField(t('Hips'), 'hips', t('enter-hips'), 'cm', errors.hips)}
                            {measureField(t('Chest'), 'chest', t('enter-chest'), 'cm', errors.chest)}
                        </div>
                        <div className="amm-row">
                            {measureField(t('Shoulders'), 'shoulders', t('enter-shoulders'), 'cm', errors.shoulders)}
                            {measureField(t('Neck'), 'neck', t('enter-neck'), 'cm', errors.neck)}
                        </div>
                        <div className="amm-row">
                            {measureField(t('left-arm'), 'leftArm', t('enter-left-arm'), 'cm', errors.leftArm)}
                            {measureField(t('right-arm'), 'rightArm', t('enter-right-arm'), 'cm', errors.rightArm)}
                        </div>
                        <div className="amm-row">
                            {measureField(t('left-thigh'), 'leftThigh', t('enter-left-thigh'), 'cm', errors.leftThigh)}
                            {measureField(t('right-thigh'), 'rightThigh', t('enter-right-thigh'), 'cm', errors.rightThigh)}
                        </div>
                        </div>

                        {/* ── Photos ── */}
                        <div className="amm-field" data-tour="amm-photos">
                            <div className="amm-photos-header">
                                <label className="amm-label">
                                    {t('photos')} ({allPhotos.length}/10)
                                </label>
                                <button
                                    type="button"
                                    className="amm-add-photo-btn"
                                    onClick={() => photoInputRef.current.click()}
                                    disabled={allPhotos.length >= 10}
                                >
                                    <AddPhotoAlternateOutlinedIcon sx={{ fontSize: 16 }} />
                                    {t('add-photo')}
                                </button>
                                <input
                                    type="file"
                                    ref={photoInputRef}
                                    style={{ display: 'none' }}
                                    accept="image/*"
                                    multiple
                                    onChange={handlePhotoAdd}
                                />
                            </div>
                            {allPhotos.length > 0 && (
                                <div className="amm-carousel">
                                <AuthenticatedImage
                                        src={allPhotos[photoCarouselIndex]}
                                        fallbackSrc={null}
                                        alt={`photo-${photoCarouselIndex + 1}`}
                                        className="amm-carousel-img"
                                    />
                                    <button
                                        type="button"
                                        className="amm-carousel-remove"
                                        onClick={() => handlePhotoRemove(photoCarouselIndex)}
                                    >
                                        <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                                    </button>
                                    {allPhotos.length > 1 && (
                                        <MobileStepper
                                            steps={allPhotos.length}
                                            position="static"
                                            activeStep={photoCarouselIndex}
                                            nextButton={
                                                <button
                                                    type="button"
                                                    className="amm-carousel-nav"
                                                    onClick={() => setPhotoCarouselIndex(prev => prev + 1)}
                                                    disabled={photoCarouselIndex === allPhotos.length - 1}
                                                >
                                                    <KeyboardArrowRight />
                                                </button>
                                            }
                                            backButton={
                                                <button
                                                    type="button"
                                                    className="amm-carousel-nav"
                                                    onClick={() => setPhotoCarouselIndex(prev => prev - 1)}
                                                    disabled={photoCarouselIndex === 0}
                                                >
                                                    <KeyboardArrowLeft />
                                                </button>
                                            }
                                        />
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ── Footer ── */}
                    <div className="amm-footer" data-tour="amm-footer">
                        <button className="amm-btn amm-btn-cancel" onClick={handleClose}>{t('Cancel')}</button>
                        {data && Object.keys(data).length > 0 && (
                            <button className="amm-btn amm-btn-delete" onClick={handleDelete}>{t('Delete')}</button>
                        )}
                        <button className="amm-btn amm-btn-save" onClick={handleSubmit}>{t('Save')}</button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AddMeasureModal;
